import type { FileInfoArr } from '../../../data/data-interfaces.ts';
import { sendEv } from '../../../data/events.ts';
import { attrNameOf, autoAttrs } from '../../../util/autoAttrs.ts';
import { asApp, withConfigurator } from '../../../util/singletons.ts';
import { cancelMessageDelayMs, startSlowOpNotice, type SlowOpNotice } from '../../../util/slowOpNotice.ts';
import { assetUrl, devlog } from '../../../util/utils.ts';
import { registerParamateInput, unregisterParamateInput } from '../../../store/paramInputRegistry.ts';
import { params, lookupParam } from '../../../store/paramStore.ts';
import { internalData } from '../../../store/store.ts';
import { applyLocalizedText } from '../../../components/base.ts';
import '../../../components/base/as-spinner.ts';
import '../../../components/inputs/buttons/as-button.ts';
import '../../../components/inputs/buttons/as-text-button.ts';
import '../../../components/inputs/as-param-warning.ts';
import '../../../components/inputs/as-help-hover.ts';
import { updateShadowDOMHelpHover } from '../../../components/inputs/input-help-hover-utils.ts';
import { HistoryTrackingMixin } from '../../../components/inputs/history-tracking-mixin.ts';
import type { InputChangeReason } from '../../../components/inputs/types.ts';
import '../../../components/overlays/as-hovercard-wrapper.ts';
import { hovercardStyles } from '../../../components/overlays/as-hovercard.ts';
import { AsText } from '../../../components/text-and-lang/as-text.ts';
import { AsFileHandle } from './fileBase.ts';
import styleText from './as-file-input.css?raw';
import { APP_DROPPED_FILES_EVENT, DROPPED_FILES_EVENT, FILES_DROPPED_EVENT } from './fileBase.ts';
import { getFileImportProviderForKind, type FileImportContext, type FileImportProvider, type ImportedFile } from './fileImportProvider.ts';

// ---- Source registry ----

// Maps source name (intercepts-dropped attribute value) to the window events it
// listens for. Drop zones share one event and are told apart by detail.source,
// so viewport and app both listen to it and filter; the deprecated per-source
// names are listened for too, so a host that dispatches one by hand still
// works. Extend here when new drop origins are introduced.
const DROP_SOURCE_EVENTS: Record<string, readonly string[]> = {
  //TODO:@REMOVE the legacy names in these two lists
  viewport: [FILES_DROPPED_EVENT, DROPPED_FILES_EVENT],
  app:      [FILES_DROPPED_EVENT, APP_DROPPED_FILES_EVENT],
  transfer: ['as-db-mesh-loaded'],
};

// ---- Category/accept conflict detection ----

// Representative file extensions per coarse category. Used only to flag an
// obvious category/accept mismatch (e.g. category="mesh" with an image-only
// accept filter). Only mesh and image are validated — text/data are too
// open-ended to check meaningfully.
const CATEGORY_EXTENSIONS: Record<string, readonly string[]> = {
  mesh:  ['stl', 'ply', 'obj', '3mf', 'step', 'stp', 'iges', 'igs', 'gltf', 'glb', 'fbx', '3ds', 'dae', 'off', 'wrl'],
  image: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'heif', 'bmp', 'svg', 'tif', 'tiff', 'avif'],
};

// Stock button/drop-zone copy per category, picked in _syncAttributes when
// the host hasn't overridden button-text/drop-text. Whole sentences, not
// interpolated — keeps localization ordinary dict-key/default-text lookups,
// same as everywhere else, with no vars/templating plumbing needed. `default`
// covers 'mesh'/'text'/'data'/unset, matching the pre-existing "Choose File"
// wording those categories always had.
const CATEGORY_UPLOAD_TEXT: Record<string, { chooseKey: string; chooseText: string; dropKey: string; dropText: string }> = {
  image: {
    chooseKey: 'fileIO.upload.chooseImage', chooseText: 'Choose Image',
    dropKey: 'fileIO.upload.dropImageHere', dropText: 'or drop image here',
  },
  default: {
    chooseKey: 'fileIO.upload.chooseFile', chooseText: 'Choose File',
    dropKey: 'fileIO.upload.dropHere', dropText: 'or drop file here',
  },
};

// ---- AsFileHandle ----
// Moved to ./as-file-handle.ts (so fileDrop.ts can share it without a cycle);
// imported above for internal use. Re-exported here so consumers can take the
// handle type straight from the component module.
export { AsFileHandle };

// ---- AsFileInput ----

/**
 * Callback registered via onRemoveFile(). Invoked once per removal with the
 * removed handle (the first of the selection when several were loaded — the
 * full set is on the as-file-removed event detail) and the remaining
 * selection. Async callbacks are the expected shape; plain sync functions are
 * accepted too (the return value is promise-wrapped either way). Rejections
 * are logged, not rethrown.
 */
export type RemoveFileCallback =
  (removedFile: AsFileHandle, fileList: readonly AsFileHandle[], reason: FileRemoveReason) => Promise<void> | void;

/**
 * Why a file was removed. `user` is the Remove button — the only one the
 * person actually asked for. `inputs-reset` is the library clearing the input
 * as part of a reset (a new project clears through this too), and `api` is an
 * app calling clear() itself. An app that does real work on removal (dropping
 * server-side state, say) usually only wants to act on `user`: a fresh session
 * has nothing to remove, and acting on a reset can push a doomed call at the
 * backend.
 */
/** The subset of InputChangeReason a file removal can carry. Kept as its own
 *  name because `as-file-removed` predates the shared vocabulary, but the
 *  values are the same words, so a listener can treat them interchangeably. */
export type FileRemoveReason = Extract<InputChangeReason, 'user' | 'inputs-reset' | 'api'>;

// One-time (per page) warning when a listener for the deprecated event name is
// attached. Shared with <as-import-group>, which re-broadcasts the inner events.
let warnedFileSelectedEvent = false;
export function warnFileSelectedListenerDeprecated(hostTag: string): void {
  if (warnedFileSelectedEvent) return;
  warnedFileSelectedEvent = true;
  console.warn(
    `[${hostTag}] the 'as-file-selected' event is deprecated — use 'as-files-selected' (selections only) ` +
    `or 'as-files-change' (any change incl. clears, 1:1 replacement) instead.`
  );
}

/**
 * <as-file-input>
 *
 * Drop zone + click-to-browse file input. Emits, on the host element:
 *
 * - `as-files-selected` — detail `{ files: AsFileHandle[] }` (never empty).
 *     Fires on a completed user selection, drop, or host-provider import — selections
 *     only, never on clear/remove (removal has its own `as-file-removed` event below).
 *     Bubbles + composed. This is the rich event most consumers want — read
 *     `e.detail.files`, NOT `e.target`.
 * - `as-files-change` — detail `{ files: AsFileHandle[] }` (empty `[]` on clear).
 *     Fires on ANY value change: every selection (alongside `as-files-selected`) and
 *     every clear — the "Remove" button and programmatic clears alike. For consumers
 *     that want one event mirroring the current selection. Bubbles + composed.
 * - `as-file-selected` — DEPRECATED alias of `as-files-change` (one-time console
 *     warning when a listener is attached on this element or an <as-import-group>).
 *     Same firing points and detail; existing listeners keep working unchanged. It was
 *     renamed because "selected" firing on the "Remove" click surprised consumers —
 *     migrate to `as-files-selected` (selections only) or `as-files-change` (1:1).
 * - `change` — no detail. Fires on every value change (select, replace, user "Remove"),
 *     mirroring native form inputs so `onchange` / `@change` / `addEventListener('change')`
 *     works on the host. Read the selection via `e.target.files` (an `AsFileHandle[]`,
 *     see the `files` getter) — NOT `e.target.value`, which is the server upload token and
 *     is empty without a `trcad-param`. NOTE: the inner `<input type=file>`'s native
 *     `change` is `composed:false` and intentionally does NOT cross the shadow boundary;
 *     the host re-emits its own. Programmatic clears (inputs-reset, project-new) are silent.
 *
 * All of the above are dispatched on the element itself (`bubbles: true, composed: true`),
 * not on `window` — listen on the component, or on `document` via bubbling.
 * - `as-file-removed` — detail `{ removedFile, removedFiles, files, reason }`. Fires whenever a
 *     non-empty selection is cleared (the "Remove" button, or any clear() with files loaded —
 *     including programmatic resets). `removedFile` is the first removed handle, `removedFiles`
 *     the full removed set, `files` the remaining selection (currently always `[]`, since
 *     removal clears the whole selection). `reason` says what did the clearing — `user` for the
 *     Remove button, `inputs-reset` for a library reset, `api` for an app's own clear() — so a
 *     listener can tell a real removal from a reset instead of guessing.
 *     Bubbles + composed. Alternatively register a callback via
 *     `input.onRemoveFile((removedFile, fileList, reason) => { ... })`.
 * - `as-input-change` — a committed change that should drive app logic (first
 *   auto-upload, non-silent clear). Detail: `{ type: 'file', value, reason, phase }`.
 * - `as-input-sync` — same detail, for a change that should not drive app logic
 *   (a silent clear, an inputs reset). See InputChangeReason.
 * - `as-file-uploaded` — detail `{ token, tokens, fileId }`. `auto-upload-file` mode only, on
 *     upload resolve. `tokens` lists every uploaded token (several with `multiple`), `token`
 *     is the first.
 * - `as-data-import` — detail `{ subcategory, fileInfo }`. Analytics, on a successful import.
 * - `as-file-import-start` — detail `{ count, subcategory }`. An import the user has to wait
 *     through has begun. Fires in every environment at the same logical point: for a native
 *     pick/drop that is the moment files are accepted (just before `as-files-change`), for a
 *     host provider it is the moment its dialog closes and conversion starts, which can be
 *     minutes ahead of any file existing. The app is busy for the whole span.
 * - `as-file-import-done` — detail `{ ok, count, subcategory }`. The same span closing, on
 *     success, failure and cancellation alike. `ok` is false when nothing was imported.
 *     Every start has exactly one done.
 *
 * Multiple files: with the `multiple` attribute, the whole selection is uploaded. A trcad
 * `open file f[]` array param (Paramate reports it as type 'array' / elementType 'file')
 * receives the full token array; a scalar `open file f` param takes only the first file,
 * with a console warning from as-configurator. Note trcad rejects an `accept` attribute on
 * `file[]` params, so for array params the accept filter must be set locally on this
 * component. In the GrabCAD env, `multiple` also switches the host's open dialog to
 * multi-select (files convert sequentially). Session restore recovers the full selection:
 * every stored file of a `file[]` param is fetched from the session zip and re-shown.
 *
 * Replacing a file: when a file is already loaded, a confirmation panel opens over
 * the drop zone before anything else happens — the old binary at that slot is gone
 * for good, so it can't be undone. Cancel (or Escape) is a complete no-op: no
 * events, no upload, the old selection stays. This is asked in the page rather
 * than with window.confirm; see _confirmReplace for why that matters.
 *
 * Every attribute below also has a matching camelCase property accessor
 * (get/set) via autoAttrs — e.g. `input.category`, `input.multiple` — mirroring
 * the attribute; boolean ones (multiple, compact, configure-on-change,
 * no-accepts-label, minimal-file-list, auto-upload-file, delete-on-clear)
 * reflect presence rather than a string value. FILE_INPUT_ATTR_SPECS is the
 * single source of truth for the list — also consumed by <as-import-group> to
 * mirror the same attributes (and generate the same properties) onto itself.
 *
 * Localization: the built-in copy ("Choose File", "or drop file here", the
 * "Accepts: …" hint, "Choose new file", "Remove", and the minimal-file-list
 * status text) renders through <as-text>/<as-button>/<as-text-button> with
 * baked-in dict keys (fileIO.upload.* in sharedLocalisation.json), so it
 * translates even without a langFile. "Choose File"/"or drop file here" are
 * themselves category-aware: `category="image"` renders "Choose Image"/"or
 * drop image here" instead, fully translated, with no extra wiring needed —
 * see CATEGORY_UPLOAD_TEXT below. `button-text`/`drop-text` override the
 * whole sentence with plain, non-localized text by default — pair either
 * with its `-dict-key` sibling (`button-text-dict-key`/`drop-text-dict-key`)
 * to make the override itself translatable, same shape as
 * `default-text`/`dict-key` everywhere else.
 */
export const FILE_INPUT_ATTR_SPECS = [
  'accept',
  { attr: 'multiple', kind: 'boolean' },
  'button-text',
  'button-text-dict-key',
  'drop-text',
  'drop-text-dict-key',
  'filetype',
  'intercepts-dropped',
  { attr: 'compact', kind: 'boolean' },
  'label-default-text',
  'label-dict-key',
  // Help tooltip beside the label, same shape as every other input: an icon that
  // shows the text on hover.
  'help-tooltip-default-text',
  'help-tooltip-dict-key',
  'help-placement',
  'trcad-param',
  // follow-param: display follows the named file param — undo/redo/restore
  //   clear or re-show the loaded file exactly like trcad-param mode — but the
  //   component does NOT upload or write the param itself. For hand-rolled
  //   flows (custom endpoints, STEP conversion, multi-param configures) where
  //   the app owns the upload. Ignored when trcad-param is set.
  'follow-param',
  { attr: 'configure-on-change', kind: 'boolean' },
  // no-accepts-label: suppress the "Accepts: ..." hint (label and compact tooltip).
  //   The accept filter itself still applies — this is display-only.
  { attr: 'no-accepts-label', kind: 'boolean' },
  // minimal-file-list: loaded state shows "N files uploaded" (or "File uploaded"
  //   without `multiple`, since the count is meaningless for a single slot)
  //   instead of file names and size.
  { attr: 'minimal-file-list', kind: 'boolean' },
  // auto-upload-file: upload only — no setParamValue, no configure. Fires 'as-file-uploaded'
  //   with { token, fileId }. First upload (empty→file) is undoable; replace is not.
  { attr: 'auto-upload-file', kind: 'boolean' },
  // file-id: overrides the fileId sent to configurator.uploadFile(). Default: trcad-param value.
  'file-id',
  // delete-on-clear: dispatch 'as-intrn-delete-file' window event when the component is cleared so
  //   consumers can sync trcad state even in auto-upload-file mode.
  { attr: 'delete-on-clear', kind: 'boolean' },
  // grabcad-stl-unit: unit sent with the GrabCAD convert request for STL files (default 'mm').
  //   Read at click time — only relevant when appEnv === 'grabcad'.
  'grabcad-stl-unit',
  // category: coarse routing hint (mesh | image | text | data, default 'mesh').
  //   Decides whether a host import provider (e.g. GrabCAD's mesh converter)
  //   claims this upload; non-matching kinds use the native file input.
  //   Read at interaction time — orthogonal to accept/filetype filtering, and
  //   distinct from the export-schema FileCategory (file role).
  'category',
] as const;

export class AsFileInput extends autoAttrs(HistoryTrackingMixin(HTMLElement), FILE_INPUT_ATTR_SPECS) {
  static readonly tag = 'as-file-input' as const;

  private _labelText!:       AsText;
  private _helpHoverEl!:     HTMLElement;
  private _container!:       HTMLElement;
  private _fileInput!:       HTMLInputElement;
  private _emptyState!:      HTMLElement;
  private _loadedState!:     HTMLElement;
  private _btnBrowse!:       HTMLElement;
  private _btnChangeText!:   HTMLElement;
  private _btnChangeIcon!:   HTMLElement;
  private _dropLabel!:       HTMLSpanElement;
  private _dropText!:        AsText;
  private _spacer!:          HTMLElement;
  private _acceptsLabel!:    AsText;
  private _fileNameText!:    AsText;
  private _fileSizeText!:    HTMLSpanElement;
  private _importingState!:  HTMLElement;
  private _importingText!:   AsText;
  private _importingStage!:  HTMLSpanElement;
  private _confirmWrapper!:  HTMLElement;

  private _isParamMissing: boolean = false;
  private _configurator: any = null;
  private _paramName: string = '';
  private _currentToken: string = '';
  // All tokens of the current selection — one entry for scalar file params,
  // several for `file[]` array params. _currentToken stays the first.
  private _currentTokens: string[] = [];
  private _currentHandle: AsFileHandle | null = null;
  private _currentFiles: AsFileHandle[] = [];
  /** The handles behind each token this input uploaded in this session, so a
   *  token the history puts back after a Remove shows its file again without
   *  a session-file lookup, which has nothing for a file uploaded this session. */
  private _uploadedHandles = new Map<string, AsFileHandle[]>();
  private _removeFileCbs: RemoveFileCallback[] = [];

  // Signature of the last category/accept conflict warned about, so a repeated
  // _syncAttributes() pass doesn't re-log the same mismatch.
  private _lastCategoryConflictSig: string = '';

  // One-time guard for the deprecated `filetype` attribute warning.
  private _warnedFiletypeDeprecated: boolean = false;

  // Tracks which window event names we're currently subscribed to
  private _listeningTo = new Set<string>();
  /** Drop origins this input adopts, from `intercepts-dropped`. */
  private _dropSources = new Set<string>();
  /** Details already handled, so one drop arriving on both the shared event and
   *  its deprecated alias is only taken once. */
  private _handledDrops = new WeakSet<object>();

  /** The upload token that goes to the trCAD param. Empty with no selection,
   *  and the first token when the param is a `file[]`. See `tokens`. */
  get value(): string { return this._currentToken; }

  /** Every upload token of the current selection: one for a scalar file param,
   *  several for a `file[]` one. Read `files` for the file data itself. */
  get tokens(): readonly string[] { return this._currentTokens; }

  override addEventListener(type: string, listener: EventListenerOrEventListenerObject | null, options?: boolean | AddEventListenerOptions): void {
    if (type === 'as-file-selected') warnFileSelectedListenerDeprecated(this.tagName.toLowerCase());
    super.addEventListener(type, listener, options);
  }

  /** Currently-selected file handles (empty when none). Lets stock `change`
   *  handlers read `e.target.files` the way a native <input type=file> exposes
   *  its FileList, here as AsFileHandle[], matching as-files-selected detail.files.
   *  The file data lives here; `value` and `tokens` are the server upload
   *  tokens, and are empty without a trcad-param. */
  get files(): readonly AsFileHandle[] { return this._currentFiles; }

  _getHistoryValue(): any { return this._currentToken; }
  override _historyEntryClearsRedo(): boolean { return !!this._currentToken; }

  private static readonly _sheet = (() => {
    const s = new CSSStyleSheet();
    s.replaceSync(styleText);
    return s;
  })();

  static get observedAttributes(): string[] {
    return FILE_INPUT_ATTR_SPECS.map(attrNameOf);
  }

  constructor() {
    super();
    const shadow = this.attachShadow({ mode: 'open' });
    // hovercardStyles first: the replace-confirm card lives in here, and the
    // copy <as-hovercard> puts on the document can't reach inside a shadow root.
    shadow.adoptedStyleSheets = [hovercardStyles, AsFileInput._sheet];
    shadow.innerHTML = `
      <div class="label-group">
        <as-text class="label-text" hidden></as-text>
        <span class="help-hover-container"></span>
      </div>
      <as-hovercard-wrapper class="confirm-wrapper" show-on-hover="false" placement="top" offset="12">
      <div class="container">
        <input type="file" class="file-input">
        <div class="empty-state">
          <img class="upload-icon" src="${assetUrl('assets/img/icons/import-arrow.svg')}" alt="">
          <div class="buttons">
            <as-button class="btn-browse"
              name="choose-file" button-id="choose-file-button" no-default-icon
              default-text="Choose File" dict-key="fileIO.upload.chooseFile">
            </as-button>
          </div>
          <span class="drop-label"><as-text dict-key="fileIO.upload.dropHere">or drop file here</as-text></span>
          <div class="spacer" hidden></div>
          <as-text class="accepts-label" hidden></as-text>
        </div>
        <div class="loaded-state" hidden>
          <div class="file-info">
            <span class="file-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></span>
            <div class="file-details">
              <as-text class="file-name-text"></as-text>
              <span class="file-size-text"></span>
            </div>
          </div>
          <div class="loaded-actions">
            <as-text-button class="btn-change-text" name="choose-new-file" button-id="choose-new-file-button" dict-key="fileIO.upload.chooseNewFile">Choose new file</as-text-button>
            <as-text-button class="btn-clear danger" name="remove-file" button-id="remove-file-button" dict-key="fileIO.upload.remove">Remove</as-text-button>
          </div>
          <as-button class="btn-change-icon" name="choose-new-file" button-id="choose-new-file-icon-button" abs-img-path="${assetUrl('assets/img/icons/import-arrow.svg')}"></as-button>
        </div>
        <div class="importing-state" hidden>
          <as-spinner></as-spinner>
          <as-text class="importing-text"></as-text>
          <span class="importing-stage"></span>
        </div>
      </div>
        <as-hovercard class="confirm-card-host">
          <div class="confirm-card">
            <as-text class="confirm-text" dict-key="fileIO.upload.replaceWarning">Replacing this file cannot be undone. Continue?</as-text>
            <div class="confirm-actions">
              <as-text-button class="confirm-cancel" name="cancel-replace" button-id="cancel-replace-button" dict-key="fileIO.upload.replaceCancel">Cancel</as-text-button>
              <as-text-button class="confirm-ok danger" name="confirm-replace" button-id="confirm-replace-button" dict-key="fileIO.upload.replaceConfirm">Replace</as-text-button>
            </div>
          </div>
        </as-hovercard>
      </as-hovercard-wrapper>
    `;
    this._labelText    = shadow.querySelector<AsText>('.label-text')!;
    this._helpHoverEl  = shadow.querySelector<HTMLElement>('.help-hover-container')!;
    this._container    = shadow.querySelector<HTMLElement>('.container')!;
    this._fileInput    = shadow.querySelector<HTMLInputElement>('.file-input')!;
    this._emptyState   = shadow.querySelector<HTMLElement>('.empty-state')!;
    this._loadedState  = shadow.querySelector<HTMLElement>('.loaded-state')!;
    this._btnBrowse    = shadow.querySelector<HTMLElement>('.btn-browse')!;
    this._btnChangeText = shadow.querySelector<HTMLElement>('.btn-change-text')!;
    this._btnChangeIcon = shadow.querySelector<HTMLElement>('.btn-change-icon')!;
    this._dropLabel    = shadow.querySelector<HTMLSpanElement>('.drop-label')!;
    this._dropText     = shadow.querySelector<AsText>('.drop-label as-text')!;
    this._spacer       = shadow.querySelector<HTMLElement>('.spacer')!;
    this._acceptsLabel = shadow.querySelector<AsText>('.accepts-label')!;
    this._fileNameText = shadow.querySelector<AsText>('.file-name-text')!;
    this._fileSizeText = shadow.querySelector<HTMLSpanElement>('.file-size-text')!;
    this._importingState = shadow.querySelector<HTMLElement>('.importing-state')!;
    this._importingText  = shadow.querySelector<AsText>('.importing-text')!;
    this._importingStage = shadow.querySelector<HTMLSpanElement>('.importing-stage')!;
    this._confirmWrapper = shadow.querySelector<HTMLElement>('.confirm-wrapper')!;

    this._setupInternalListeners();
  }

  connectedCallback(): void {
    this._syncAttributes();
    this._syncSourceListeners();
    this._syncRestoreListener();
    this._updateParamRegistration();
    this._requestConfigurator();
    window.addEventListener('as-inputs-reset', this._handleInputsReset);
    window.addEventListener('as-restore-param-values', this._handleRestoreParamValues);
    window.addEventListener('as-restore-input-values', this._handleRestoreInputValues);
    this._unsubParams = params.subscribeAll(() => this._checkParamPresence());
    this.addEventListener('as-paramate-value-resolved', this._handleParamateValueResolved as EventListener);
    this._registerHistoryTracking();
  }

  disconnectedCallback(): void {
    // An unanswered replace question goes away with the component — treat it as
    // a cancel so the pending _dispatch unwinds instead of hanging forever.
    this._settleReplaceConfirm(false);
    this._syncSourceListeners();
    window.removeEventListener('paramate-configurator-after-restore', this._handleAfterRestore);
    window.removeEventListener('as-inputs-reset', this._handleInputsReset);
    window.removeEventListener('as-restore-param-values', this._handleRestoreParamValues);
    window.removeEventListener('as-restore-input-values', this._handleRestoreInputValues);
    this._unsubParams?.();
    this._unsubParams = undefined;
    this.removeEventListener('as-paramate-value-resolved', this._handleParamateValueResolved as EventListener);
    this._unregisterHistoryTracking();

    if (this._paramName) {
      unregisterParamateInput(this._paramName, this);
    }
  }

  attributeChangedCallback(name: string, _old: string | null, _new: string | null): void {
    switch (name) {
      case 'accept':
      case 'multiple':
      case 'button-text':
      case 'button-text-dict-key':
      case 'drop-text':
      case 'drop-text-dict-key':
      case 'filetype':
      case 'compact':
      case 'label-default-text':
      case 'label-dict-key':
      case 'help-tooltip-default-text':
      case 'help-tooltip-dict-key':
      case 'help-placement':
      case 'configure-on-change':
      case 'category':
      case 'no-accepts-label':
        this._syncAttributes();
        break;
      case 'minimal-file-list':
        this._renderLoadedInfo();
        break;
      case 'intercepts-dropped':
        this._syncSourceListeners();
        break;
      case 'trcad-param':
        this._syncRestoreListener();
        this._updateParamRegistration();
        this._requestConfigurator();
        break;
      case 'follow-param':
        this._syncRestoreListener();
        this._checkParamPresence();
        break;
    }
  }

  /** The param the display follows: trcad-param, or follow-param for inputs
   *  whose upload is app-owned (see FILE_INPUT_ATTR_SPECS). '' when neither. */
  private _followedParamName(): string {
    return (this.getAttribute('trcad-param') ?? this.getAttribute('follow-param') ?? '').trim();
  }

  private _updateParamRegistration(): void {
    if (this._paramName) {
      unregisterParamateInput(this._paramName, this);
    }
    this._paramName = (this.getAttribute('trcad-param') ?? '').trim();
    if (this._paramName) {
      registerParamateInput(this._paramName, this);
    }
  }

  /** Was registered as `this.clear` directly, which handed the Event object in
   *  as the options bag — harmless, since every field read off it was
   *  undefined, but it left no way to say why the clear happened. */
  // A reset comes from a new session or a project load. The params are the
  // library's business there: the new session starts empty and a load sets
  // them itself. So the input drops its display and nothing else. A configure
  // here used to be queued behind the reinit and land on the fresh session as
  // its first undo entry.
  private _handleInputsReset = () => {
    this.clear({ silenceChange: true, reason: 'inputs-reset', keepParam: true });
  };

  /** Register callback(s) invoked when a loaded file is removed (see the
   *  as-file-removed event docs above). Accepts a single callback or an array;
   *  each call replaces the previously registered set. Pass null to
   *  unregister. Callbacks run concurrently and are each promise-wrapped, so
   *  sync and async functions both work. */
  public onRemoveFile(cb: RemoveFileCallback | RemoveFileCallback[] | null): void {
    this._removeFileCbs = cb == null ? [] : Array.isArray(cb) ? [...cb] : [cb];
  }

  /** Clear the current file selection. Set silenceChange:true to report on
   *  as-input-sync instead of as-input-change.
   *  `reason` travels to as-file-removed and onRemoveFile so a listener can tell a
   *  user removal from a reset. Defaults to 'api' — the library's own callers pass
   *  their own.
   *  `keepParam` clears the display only: no configure of the param to empty and
   *  no token release. For a reset, where the session or project that caused it
   *  owns the params. */
  public clear = (opt?: { silenceChange?: boolean; reason?: FileRemoveReason; keepParam?: boolean }): void => {
    const silenceChange = opt?.silenceChange ?? true; // Default to silent
    const reason: FileRemoveReason = opt?.reason ?? 'api';
    const removedFiles = this._currentFiles;
    this._resetToEmpty();

    const paramName = this.getAttribute('trcad-param');
    const isAutoUpload = this.hasAttribute('auto-upload-file');

    if (opt?.keepParam) {
      // display only
    } else if (paramName && !isAutoUpload) {
      // Normal trcad-param mode: reconfigure to empty. The token stays with the
      // history, which re-activates it when the clear is undone and releases it
      // when the entry that uploaded it is truncated or the stack is cleared.
      sendEv(window, 'as-configure', {
        detail: {
          params: {
            [paramName.startsWith('main:') ? paramName : `main:${paramName}`]: { value: '' }
          },
          skipHistory: silenceChange
        }
      });
    } else if (this.hasAttribute('delete-on-clear')) {
      // auto-upload-file or standalone: dispatch as-intrn-delete-file so consumers can sync trcad state.
      sendEv(window, 'as-intrn-delete-file', {
        detail: { fileParamName: this._fileId() }
      });
    }

    const detail = { type: 'file' as const, value: this._currentToken, reason };
    sendEv(this, silenceChange ? 'as-input-sync' : 'as-input-change', {
      detail: { ...detail, phase: silenceChange ? 'sync' : 'change' },
    });
    if (!silenceChange) {
      sendEv(this, 'change');
    }

    // as-files-selected is selection-only and deliberately NOT dispatched on
    // clear; the any-change signal here is as-files-change (empty files), and
    // removal specifically is as-file-removed below. The deprecated
    // as-file-selected keeps its legacy behaviour of also firing on clear —
    // exactly why it was renamed.
    sendEv(this, 'as-files-change', {
      detail: { files: [] }
    });
    sendEv(this, 'as-file-selected', {
      detail: { files: [] }
    });

    // Removal notification: only when something was actually loaded, so clears
    // of an already-empty input stay silent.
    if (removedFiles.length) {
      const removedFile = removedFiles[0];
      sendEv(this, 'as-file-removed', {
        detail: { removedFile, removedFiles, files: this._currentFiles, reason }
      });
      for (const cb of this._removeFileCbs) {
        Promise.resolve()
          .then(() => cb(removedFile, this._currentFiles, reason))
          .catch(err => console.error('[as-file-input] onRemoveFile callback failed:', err));
      }
    }
  };

  private _syncAttributes(): void {
    if (!this._fileInput) return;
    const isCompact = this.hasAttribute('compact');
    this._container.classList.toggle('compact', isCompact);
    this._spacer.hidden = !isCompact;

    const label = this.getAttribute('label-default-text');
    const labelDictKey = this.getAttribute('label-dict-key');
    applyLocalizedText(this._labelText, labelDictKey, label ?? '');
    this._labelText.hidden = label === null && !labelDictKey && !this._isParamMissing;
    updateShadowDOMHelpHover(this, this._helpHoverEl);

    // Use the effective filter (incl. any category-derived default) so the OS
    // picker greys out non-matching files consistently with drop validation.
    this._fileInput.accept   = this._effectiveFiletype() || '*/*';
    this._fileInput.multiple = this.hasAttribute('multiple');
    // Stock "Choose File"/"or drop file here" copy is category-aware (see
    // CATEGORY_UPLOAD_TEXT above) — category="image" gets "Choose Image"/"or
    // drop image here", fully translated, with no extra wiring needed. An
    // explicit button-text/drop-text override opts back out of this — it's
    // now app-specific copy, same rule as everywhere else in this codebase —
    // unless paired with its own *-dict-key, same as any other default-text/
    // dict-key pair.
    const stock = CATEGORY_UPLOAD_TEXT[(this.getAttribute('category') ?? '').trim().toLowerCase()] ?? CATEGORY_UPLOAD_TEXT.default;

    const buttonText = this.getAttribute('button-text');
    const buttonTextDictKey = this.getAttribute('button-text-dict-key');
    applyLocalizedText(this._btnBrowse, buttonTextDictKey ?? (buttonText == null ? stock.chooseKey : null), buttonText ?? stock.chooseText);

    const dropText = this.getAttribute('drop-text');
    const dropTextDictKey = this.getAttribute('drop-text-dict-key');
    applyLocalizedText(this._dropText, dropTextDictKey ?? (dropText == null ? stock.dropKey : null), dropText ?? stock.dropText);

    // Warning component
    const labelGroup = this.shadowRoot!.querySelector('.label-group')!;
    let warning = labelGroup.querySelector('as-param-warning');
    if (!warning) {
      warning = document.createElement('as-param-warning');
      labelGroup.appendChild(warning);
    }
    warning.setAttribute('trcad-param', this._paramName);
    warning.setAttribute('is-missing', String(this._isParamMissing));

    const filetype = this._effectiveFiletype();
    if (filetype && !this.hasAttribute('no-accepts-label')) {
      const typeList = filetype.split(',').map(s => s.trim()).join(', ');
      const acceptsText = `Accepts: ${typeList}`;
      if (isCompact) {
        // as-tooltip-wrapper has no {0}-substitution mechanism, so a dynamic
        // list like this can't route through a dict key here — the tooltip
        // stays English-only in compact mode.
        this._btnBrowse.setAttribute('tooltip-default-text', acceptsText);
        this._btnBrowse.setAttribute('tooltip-placement', 'bottom');
        // btn-change-icon is the only visible action button in compact
        // loaded state (see .container.compact .btn-change-icon in the
        // stylesheet), so it needs the same tooltip as btn-browse or the
        // hint disappears the moment a file is imported.
        this._btnChangeIcon.setAttribute('tooltip-default-text', acceptsText);
        this._btnChangeIcon.setAttribute('tooltip-placement', 'bottom');
        this._acceptsLabel.hidden = true;
      } else {
        this._btnBrowse.removeAttribute('tooltip-default-text');
        this._btnChangeIcon.removeAttribute('tooltip-default-text');
        this._acceptsLabel.setAttribute('default-text', acceptsText);
        this._acceptsLabel.setAttribute('dict-key', 'fileIO.upload.accepts');
        this._acceptsLabel.vars = [typeList];
        this._acceptsLabel.hidden = false;
      }
    } else {
      this._btnBrowse.removeAttribute('tooltip-default-text');
      this._btnChangeIcon.removeAttribute('tooltip-default-text');
      this._acceptsLabel.hidden = true;
    }

    this._checkCategoryAcceptConflict();
  }

  /** Coarse categories implied by the file types in an accept string. Reads
   *  extensions (".png") and top-level MIME groups ("image/*", "model/stl"). */
  private _acceptCategories(accept: string): Set<string> {
    const cats = new Set<string>();
    for (const raw of accept.split(',')) {
      const tok = raw.trim().toLowerCase();
      if (!tok || tok === '*/*') continue;
      if (tok.includes('/')) {
        const top = tok.split('/')[0];
        if (top === 'image') cats.add('image');
        if (top === 'model') cats.add('mesh');
        continue;
      }
      const ext = tok.startsWith('.') ? tok.slice(1) : tok;
      for (const [cat, exts] of Object.entries(CATEGORY_EXTENSIONS)) {
        if (exts.includes(ext)) cats.add(cat);
      }
    }
    return cats;
  }

  /** Warn when an explicit category="mesh"|"image" disagrees with the accept
   *  filter (e.g. category="mesh" but accept lists image types). The local
   *  category is authoritative and routing is unaffected — this only surfaces a
   *  likely authoring/param mistake. Deduped against the last warned mismatch. */
  private _checkCategoryAcceptConflict(): void {
    const category = (this.getAttribute('category') ?? '').trim().toLowerCase();
    const accept = (this.getAttribute('accept') ?? '').trim();
    const opposite = category === 'mesh' ? 'image' : category === 'image' ? 'mesh' : '';

    let conflict = '';
    if (opposite && accept && accept !== '*/*' && this._acceptCategories(accept).has(opposite)) {
      conflict = `${category}|${this._normalizeAccept(accept)}`;
    }
    if (conflict && conflict !== this._lastCategoryConflictSig) {
      const param = this.getAttribute('trcad-param');
      console.warn(
        `[as-file-input] category="${category}" conflicts with accept="${accept}": ` +
        `the accept filter includes ${opposite} file types` +
        (param ? ` (trcad-param "${param}")` : '') + '.'
      );
    }
    this._lastCategoryConflictSig = conflict;
  }

  /** See AsInputBase._requestConfigurator: same wait, different follow-up. */
  private _requestConfigurator(): void {
    const paramName = this.getAttribute('trcad-param');
    if (!paramName) return;

    if (this._configurator) {
      this._checkParamPresence();
      return;
    }

    withConfigurator(this, (configurator) => {
      this._configurator = configurator;
      this._checkParamPresence();
    });
  }

  private _checkParamPresence(): void {
    if (!this._configurator?.params) return;
    const paramName = this._followedParamName();
    if (!paramName) return;

    const param = lookupParam(this._configurator.params, paramName);
    this._isParamMissing = param === undefined;
    if (param) this._syncAcceptFromParam(param);
    this._syncAttributes();
  }

  /** A trcad file param may carry its own `accept` filter (HTML accept syntax,
   *  e.g. ".stl, .ply"). Adopt it when the host hasn't set an `accept` locally.
   *  If both are present and disagree, the local attribute wins (host intent is
   *  explicit) but we warn so the mismatch is visible.
   *  Note: trcad rejects `accept` on `file[]` array params, so array-linked
   *  inputs never receive one here — set the filter locally instead. */
  private _syncAcceptFromParam(param: any): void {
    const paramAccept = typeof param?.accept === 'string' ? param.accept.trim() : '';
    if (!paramAccept) return;
    const localAccept = (this.getAttribute('accept') ?? '').trim();
    if (!localAccept) {
      this.setAttribute('accept', paramAccept);
      return;
    }
    if (this._normalizeAccept(localAccept) !== this._normalizeAccept(paramAccept)) {
      console.warn(
        `[as-file-input] accept conflict for trcad-param "${this.getAttribute('trcad-param')}": ` +
        `local accept="${localAccept}" overrides param accept="${paramAccept}".`
      );
    }
  }

  /** Order/space/case-insensitive canonical form for comparing accept strings. */
  private _normalizeAccept(s: string): string {
    return s.split(',').map(t => t.trim().toLowerCase()).filter(Boolean).sort().join(',');
  }

  /** Unsubscribe for the param-table subscription taken in connectedCallback. */
  private _unsubParams?: () => void;

  /** Non-empty upload tokens of a file param value: a scalar param holds
   *  '' | token, a file[] param a token array (empty strings when unset). */
  private _tokensOf(value: any): string[] {
    const raw = Array.isArray(value) ? value : [value];
    return raw.filter((v: any) => typeof v === 'string' && v.length > 5);
  }

  /** Whether the given token list matches the current selection's tokens. */
  private _sameTokens(tokens: string[]): boolean {
    const current = this._currentTokens.length
      ? this._currentTokens
      : this._currentToken ? [this._currentToken] : [];
    return tokens.length === current.length && tokens.every((t, i) => t === current[i]);
  }

  /** Reset the selection state and show the empty drop zone. */
  private _resetToEmpty(): void {
    this._currentToken = '';
    this._currentTokens = [];
    this._currentHandle = null;
    this._currentFiles = [];
    this._fileInput.value = '';
    this._emptyState.hidden = false;
    this._loadedState.hidden = true;
    this._fileNameText.textContent = '';
    this._fileSizeText.textContent = '';
  }

  private _handleParamateValueResolved = (ev: CustomEvent): void => {
    const { value } = ev.detail;
    if (value === undefined) return;

    const tokens = this._tokensOf(value);
    const isEmpty = value === '' || value === null
      || (Array.isArray(value) && tokens.length === 0);
    if (isEmpty) {
      // Only clear if we actually have something to clear
      if (this._currentToken || this._currentHandle) this._resetToEmpty();
    } else if (tokens.length) {
      if (this._sameTokens(tokens) && this._currentHandle) return; // Already showing these
      this._currentToken = tokens[0];
      this._currentTokens = tokens;
      const known = this._uploadedHandles.get(tokens.join('|'));
      if (known) { this._showLoadedState(known); return; }
      const paramName = this._followedParamName();
      if (paramName) this._fetchAndShowSessionFile(paramName);
    }
  };

  private _syncSourceListeners(): void {
    const active = this.isConnected && this.hasAttribute('intercepts-dropped');
    if (!active) {
      for (const evName of this._listeningTo) window.removeEventListener(evName, this._handleSourceDrop);
      this._listeningTo.clear();
      this._dropSources.clear();
      return;
    }
    const val = (this.getAttribute('intercepts-dropped') ?? '').trim();
    const sourceNames = val ? val.split(',').map(s => s.trim()).filter(Boolean) : Object.keys(DROP_SOURCE_EVENTS);
    this._dropSources = new Set(sourceNames.filter(s => s in DROP_SOURCE_EVENTS));
    const wanted = new Set([...this._dropSources].flatMap(s => DROP_SOURCE_EVENTS[s]));
    for (const evName of this._listeningTo) {
      if (!wanted.has(evName)) {
        window.removeEventListener(evName, this._handleSourceDrop);
        this._listeningTo.delete(evName);
      }
    }
    for (const evName of wanted) {
      if (!this._listeningTo.has(evName)) {
        window.addEventListener(evName, this._handleSourceDrop);
        this._listeningTo.add(evName);
      }
    }
  }

  private _setupInternalListeners(): void {
    this._btnBrowse.addEventListener('as-button-click', () => this._openFilePicker());
    this._btnChangeText.addEventListener('click', () => this._openFilePicker());
    this._btnChangeIcon.addEventListener('as-button-click', () => this._openFilePicker());
    // User-initiated Remove fires change + as-input-change; passing the PointerEvent
    // straight to clear() would be read as a (silent) opt, so wrap it explicitly.
    this.shadowRoot!.querySelector('.btn-clear')?.addEventListener('click', () => this.clear({ silenceChange: false, reason: 'user' }));
    this.shadowRoot!.querySelector('.confirm-ok')?.addEventListener('as-button-click', () => this._settleReplaceConfirm(true));
    this.shadowRoot!.querySelector('.confirm-cancel')?.addEventListener('as-button-click', () => this._settleReplaceConfirm(false));
    this._fileInput.addEventListener('change', () => {
      const files = this._fileInput.files;
      if (files?.length) this._processFiles(Array.from(files));
    });
    this._container.addEventListener('dragover', (e) => {
      if (e.dataTransfer?.types.includes('Files')) { e.preventDefault(); this._container.classList.add('drag-active'); }
    });
    this._container.addEventListener('dragleave', () => this._container.classList.remove('drag-active'));
    this._container.addEventListener('drop', (e) => {
      e.preventDefault(); e.stopPropagation(); this._container.classList.remove('drag-active');
      const files = e.dataTransfer?.files;
      if (files?.length) this._processFiles(Array.from(files));
    });
  }

  /** Coarse routing kind for host import providers. Defaults to 'mesh' — the
   *  established behaviour, since the only host provider (GrabCAD) converts
   *  meshes. Set category="image" | "text" | "data" to bypass the host
   *  provider and use the native file input. Orthogonal to accept/filetype. */
  private _category(): string {
    return (this.getAttribute('category') ?? 'mesh').trim().toLowerCase();
  }

  /** Host import provider for the current env, but only if it claims this
   *  component's category. Null otherwise (→ native file input). */
  private _importProvider(): FileImportProvider | null {
    return getFileImportProviderForKind(internalData.appEnv, this._category());
  }

  /** Open the file picker programmatically, honouring any host import provider
   *  (e.g. GrabCAD's native open/convert dialog) for the current env + category.
   *  Hosts that hide this component and drive it from their own button MUST call
   *  this instead of clicking the inner <input> directly — reaching into the
   *  shadow DOM bypasses provider routing and always opens the plain browser
   *  dialog. */
  public openFilePicker(): void {
    this._openFilePicker();
  }

  private _openFilePicker(): void {
    // The replace panel is up and waiting on an answer — a second picker here
    // would leave that first request dangling.
    if (this._confirmResolve) return;
    const provider = this._importProvider();
    if (provider) {
      void this._runProviderFlow(() => provider.pickFile(this._providerContext()));
      return;
    }
    this._fileInput.click();
  }

  // ---- Replace confirmation ----
  //
  // Deliberately not window.confirm. That is a native modal: it parks the
  // renderer and takes OS focus off the page, and an Electron host (GrabCAD)
  // hands focus back to the window without re-plumbing it into the web
  // contents. The page looks fine and still reports an activeElement, but no
  // keystrokes reach it — text inputs stay dead until the user blurs the window
  // and comes back. So the question is asked in-page, in a hovercard panel.

  private _confirmResolve: ((ok: boolean) => void) | null = null;

  /** Ask before replacing a loaded file. Resolves false when the user cancels
   *  (Cancel, Escape, or the component going away), and the caller must then
   *  do nothing at all — the old file stays as it was. */
  private _confirmReplace(): Promise<boolean> {
    this._settleReplaceConfirm(false); // a fresh ask supersedes any open one
    return new Promise<boolean>(resolve => {
      this._confirmResolve = resolve;
      this._confirmWrapper.setAttribute('show', '');
      window.addEventListener('keydown', this._handleConfirmKeydown);
    });
  }

  private _settleReplaceConfirm(ok: boolean): void {
    const resolve = this._confirmResolve;
    if (!resolve) return;
    this._confirmResolve = null;
    window.removeEventListener('keydown', this._handleConfirmKeydown);
    this._confirmWrapper.removeAttribute('show');
    resolve(ok);
  }

  private _handleConfirmKeydown = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') this._settleReplaceConfirm(false);
  };

  // ---- Host import providers (plugin pattern) ----
  // Host environments (e.g. GrabCAD) replace the standard open/drop flow with
  // their own native dialogs and file conversion. Providers self-register in
  // their plugin module (see fileImportProvider.ts) so this component stays
  // host-agnostic. Looked up at interaction time so no env subscription is needed.

  private _providerBusy = false;
  private _providerAbort: AbortController | null = null;

  private _providerContext(): FileImportContext {
    const provider = this._importProvider();
    // Only hand over a signal a provider actually acts on, so `cancellable`
    // stays the single source of truth for whether Cancel is offered.
    this._providerAbort = provider?.cancellable ? new AbortController() : null;
    return {
      getAttribute: (name) => this.getAttribute(name),
      onProgress: (p) => this._showImportStage(p),
      onImportProgress: (done, total) => this._beginOrUpdateImport(done, total),
      signal: this._providerAbort?.signal,
    };
  }

  /** If the active provider claims the dropped files (e.g. to convert them),
   *  run its flow and return true. Unclaimed drops take the normal flow. */
  private _interceptDropForProvider(blobs: Blob[]): boolean {
    const provider = this._importProvider();
    if (!provider) return false;
    const flow = provider.claimDrop(blobs, this._providerContext());
    if (!flow) {
      // Not claimed — drop the controller _providerContext() just built, or a
      // later native import would offer a Cancel wired to nothing.
      this._providerAbort = null;
      return false;
    }
    void this._runProviderFlow(flow);
    return true;
  }

  private async _runProviderFlow(flow: () => Promise<ImportedFile[] | null>): Promise<void> {
    if (this._providerBusy) return; // GrabCAD's protocol has no correlation id — one flow at a time
    this._providerBusy = true;
    try {
      const results = await flow();
      if (!results?.length) {
        // Dialog dismissed, or the conversion was cancelled part-way. If it had
        // got far enough to open an import, this is what closes it — otherwise
        // nothing was ever opened and _endImport is a no-op.
        this._endImport(false);
        return;
      }
      // The provider already filtered and converted the selection, so the
      // results bypass _matchesFiletype.
      const handles = results.map(r =>
        new AsFileHandle(new File([r.data], r.filename), r.filename, r.filetype));
      // Awaited: _dispatch may stop to ask about a replace, and the finally
      // below would otherwise tear the import down while the panel is still up.
      await this._dispatch(handles);
    } catch (err: any) {
      devlog('[as-file-input] import provider failed:', err);
      this._endImport(false);
      sendEv(this, 'as-data-import', {
        detail: { subcategory: internalData.appEnv, error: err?.message ?? String(err) }
      });
      sendEv(asApp(), 'as-notify', {
        bubbles: false,
        detail: { defaultText: 'File import failed: {0}', dictKey: 'notifications.import.failed', vars: [err?.message ?? String(err)], type: 'error', persist: true }
      });
    } finally {
      this._providerBusy = false;
      this._providerAbort = null;
      // Safety net. The paths above close the import themselves; this catches
      // anything that slipped through, because an import left open holds the
      // app busy forever.
      if (this._importOpen && !this._uploadPending) this._endImport(false);
    }
  }

  // ---- Import lifecycle ----
  //
  // An import is the whole span the user waits through: from the moment they
  // stop choosing files to the moment the upload settles. That span is the same
  // in every environment — a host provider (GrabCAD) just adds a conversion leg
  // in front of the upload, which is why this is bracketed here rather than in
  // the provider path alone.
  //
  // While it is open the app holds a busy key (so the header and step panels
  // lock, as they do for a configure), the component shows its own spinner, and
  // a slow one raises the same Cancel/Dismiss notice a slow configure does.
  //
  // Every _beginImport needs a matching _endImport, on success and failure
  // alike, or the app stays busy forever.

  private _importOpen = false;
  /** True between firing `as-intrn-upload-file` and its resolve/reject. The import has to
   *  outlive _runProviderFlow in that window — the upload is still running. */
  private _uploadPending = false;
  private _importBusyKey = '';
  private _importTotal = 0;
  private _importNotice: SlowOpNotice | null = null;

  /** Called on the provider's first progress report and again as files land.
   *  The first call opens the import; later ones just refresh the count. */
  private _beginOrUpdateImport(done: number, total: number): void {
    if (!this._importOpen) this._beginImport(total);
    this._importTotal = total;
    this._renderImportingState(done, total);
  }

  private _beginImport(total: number): void {
    if (this._importOpen) return;
    this._importOpen = true;
    this._importTotal = total;
    this._importBusyKey = `file-import:${this._importKey()}`;

    sendEv(window, 'as-app-busy-request', {
      detail: { key: this._importBusyKey, busy: true }
    });

    // Only offer Cancel when something can act on it. A provider that can't
    // abort, or a plain upload once it is in flight, would give the user a
    // button that does nothing.
    const abort = this._providerAbort;
    this._importNotice = startSlowOpNotice({
      delayMs: cancelMessageDelayMs(this, 10),
      defaultText: 'This import is taking a while. Would you like to cancel it?',
      dictKey: 'notifications.import.timeout',
      onCancel: abort ? () => abort.abort() : () => {},
    });

    this._renderImportingState(0, total);
    sendEv(this, 'as-file-import-start', {
      detail: { count: total, subcategory: this._importSubcategory() }
    });
  }

  private _endImport(ok: boolean): void {
    if (!this._importOpen) return;
    this._importOpen = false;

    sendEv(window, 'as-app-busy-request', {
      detail: { key: this._importBusyKey, busy: false }
    });
    this._importNotice?.close();
    this._importNotice = null;
    this._hideImportingState();

    sendEv(this, 'as-file-import-done', {
      detail: { ok, count: this._importTotal, subcategory: this._importSubcategory() }
    });
  }

  /** Busy keys are ref-counted by name, so two file inputs importing at once
   *  must not share one. Falls back to a generated key when the element has no
   *  id of its own. */
  private _importKey(): string {
    if (this.id) return this.id;
    this._generatedImportKey ??= `anon-${++AsFileInput._importSeq}`;
    return this._generatedImportKey;
  }
  private _generatedImportKey: string | null = null;
  private static _importSeq = 0;

  private _importSubcategory(): string {
    return this._importProvider() ? (internalData.appEnv ?? 'local') : 'local';
  }

  /** Host progress is a stage name on current GrabCAD builds ('converting',
   *  'reading'). Legacy hosts send a number, but the protocol never says what
   *  it is a fraction of — so a number is logged and not shown, rather than
   *  rendered as a percentage we'd be guessing at. */
  private _showImportStage(progress: string | number): void {
    devlog('[as-file-input] import provider progress:', progress);
    if (!this._importOpen || typeof progress !== 'string') return;
    this._importingStage.textContent = progress;
  }

  private _renderImportingState(done: number, total: number): void {
    if (total > 1) {
      this._importingText.setAttribute('dict-key', 'fileIO.upload.importingCount');
      this._importingText.setAttribute('default-text', `Importing file ${done + 1} of ${total}`);
      this._importingText.vars = [String(Math.min(done + 1, total)), String(total)];
    } else {
      this._importingText.setAttribute('dict-key', 'fileIO.upload.importing');
      this._importingText.setAttribute('default-text', 'Importing file');
      this._importingText.vars = null;
    }
    this._emptyState.hidden = true;
    this._loadedState.hidden = true;
    this._importingState.hidden = false;
    this._container.classList.add('importing');
  }

  private _hideImportingState(): void {
    this._importingState.hidden = true;
    this._importingStage.textContent = '';
    this._container.classList.remove('importing');
    // Back to whichever state the current selection implies — a cancelled
    // import must not strand a previously loaded file behind the spinner.
    const loaded = this._currentFiles.length > 0;
    this._loadedState.hidden = !loaded;
    this._emptyState.hidden = loaded;
  }

  private _syncRestoreListener(): void {
    window.removeEventListener('paramate-configurator-after-restore', this._handleAfterRestore);
    if (this.isConnected && this._followedParamName()) window.addEventListener('paramate-configurator-after-restore', this._handleAfterRestore);
  }

  private _handleAfterRestore = () => {
    const paramName = this._followedParamName();
    if (paramName) this._fetchAndShowSessionFile(paramName);
  };

  private _handleRestoreParamValues = (ev: Event) => {
    const { params } = (ev as CustomEvent).detail ?? {};
    const paramName = this._followedParamName();
    if (!paramName || !params) return;
    const fullName = paramName.startsWith('main:') ? paramName : `main:${paramName}`;
    const shortName = paramName.startsWith('main:') ? paramName.slice(5) : paramName;
    const val = params[fullName] ?? params[shortName];
    if (val === undefined) return;
    const tokens = this._tokensOf(val);
    if (!tokens.length) {
      this._resetToEmpty();
    } else if (!this._sameTokens(tokens)) {
      this._currentToken = tokens[0];
      this._currentTokens = tokens;
      this._fetchAndShowSessionFile(paramName);
    }
  };

  /** The key an upload is tracked under, and so the key a delete must name. */
  private _fileId(): string {
    return this.getAttribute('file-id') || this.getAttribute('trcad-param') || this.id;
  }

  /** Handles undo for auto-upload-file mode ('input' history action restoring to null/empty).
   *  Clears the component and deletes the uploaded token from the server. */
  private _handleRestoreInputValues = (ev: Event): void => {
    const { inputs } = (ev as CustomEvent).detail ?? {};
    if (!inputs || !this.id || inputs[this.id] === undefined) return;
    const val = inputs[this.id];
    if (val) return; // redo path — restoring to a token value is not yet supported for auto-upload-file
    const prevToken = this._currentToken;
    this._resetToEmpty();
    // Always delete the server-side file token when undoing an auto-upload.
    if (prevToken) {
      const fileId = this._fileId();
      sendEv(window, 'as-intrn-delete-file', { detail: { fileParamName: fileId } });
      sendEv(window, 'as-history-truncate-redo');
    }
  };

  private _fetchAndShowSessionFile(paramName: string) {
    sendEv(window, 'as-get-session-file', {
      detail: {
        paramName,
        resolve: (result: any) => {
          if (result) {
            // result.files lists every stored file (several for file[] array
            // params); older resolvers may send only the flat { blob, filename }.
            const stored: Array<{ blob: Blob; filename: string }> =
              Array.isArray(result.files) && result.files.length ? result.files : [result];
            const handles = stored.map(f => new AsFileHandle(f.blob, f.filename));
            this._currentHandle = handles[0];
            this._showLoadedState(handles);
          } else {
            // Only revert if we don't already have a handle (prevent flicker after upload)
            if (!this._currentHandle) {
              this._fileInput.value = '';
              this._emptyState.hidden = false;
              this._loadedState.hidden = true;
            }
          }
        },
        reject: (err: any) => {
          devlog('[as-file-input] session file restore failed:', err);
          if (!this._currentHandle) {
            this._fileInput.value = '';
            this._emptyState.hidden = false;
            this._loadedState.hidden = true;
          }
        },
      },
    });
  }

  /** Tell the user that one or more selected/dropped files were rejected by the
   *  accept filter, naming them and the accepted types. Routed through as-app's
   *  notification system (same pattern as _runProviderFlow). Without an as-app
   *  host (standalone/demo) it is a silent no-op. */
  private _notifyRejected(rejectedNames: string[]): void {
    if (!rejectedNames.length) return;
    const accepts = this._effectiveFiletype();
    const acceptsList = accepts ? accepts.split(',').map(s => s.trim()).join(', ') : '';
    const names = rejectedNames.map(n => `"${n}"`).join(', ');
    // One key per shape, so a translator owns the whole sentence: one file or
    // several, with or without the accepted-types clause.
    const suffix = acceptsList ? 'WithAccepts' : '';
    const dictKey = `notifications.import.rejectedType.${rejectedNames.length === 1 ? 'one' : 'many'}${suffix}`;
    const defaults: Record<string, string> = {
      'notifications.import.rejectedType.one': '{0} is not an accepted file type.',
      'notifications.import.rejectedType.oneWithAccepts': '{0} is not an accepted file type. Accepts: {1}.',
      'notifications.import.rejectedType.many': 'These files are not accepted types: {0}.',
      'notifications.import.rejectedType.manyWithAccepts': 'These files are not accepted types: {0}. Accepts: {1}.',
    };
    sendEv(asApp(), 'as-notify', {
      bubbles: false,
      detail: { defaultText: defaults[dictKey], dictKey, vars: [names, acceptsList], type: 'error', persist: true }
    });
  }

  /** Partition items by the accept filter, notifying the user about any rejected
   *  by type. Returns the accepted subset (valid files still proceed; only the
   *  rejected ones are dropped, with a notice). */
  private _partitionByFiletype<T>(items: T[], nameOf: (t: T) => string, typeOf: (t: T) => string): T[] {
    const accepted: T[] = [];
    const rejected: string[] = [];
    for (const it of items) {
      if (this._matchesFiletype(nameOf(it), typeOf(it))) accepted.push(it);
      else rejected.push(nameOf(it));
    }
    this._notifyRejected(rejected);
    return accepted;
  }

  private _handleSourceDrop = (e: Event) => {
    const detail = (e as CustomEvent).detail;
    // Drop zones all share one event, so an input that only adopts one of them
    // has to skip the rest. A source-less detail came from a hand-dispatched
    // legacy event, and the event name already narrowed it.
    if (detail?.source && !this._dropSources.has(detail.source)) return;
    if (detail && typeof detail === 'object') {
      if (this._handledDrops.has(detail)) return;
      this._handledDrops.add(detail);
    }
    let raw = detail?.files;
    if (!raw && detail?.stl) raw = [detail.stl];
    if (!raw?.length) return;
    // Sources may send AsFileHandle[] (drop zones), native File/Blob (mesh
    // transfer's detail.stl), or the legacy { filename, filetype, blob } shape.
    // Normalize everything to AsFileHandle so the rest of the path is uniform.
    const handles: AsFileHandle[] = raw.map((f: any) =>
      f instanceof AsFileHandle ? f
      : f instanceof Blob       ? new AsFileHandle(f)
      :                           new AsFileHandle(f.blob, f.filename, f.filetype));
    if (this._interceptDropForProvider(handles.map(h => h.blob))) return;
    const accepted = this._partitionByFiletype(handles, h => h.name, h => h.type);
    if (accepted.length) this._dispatch(accepted);
  };

  setFiles(files: File | File[]): void {
    this._processFiles(Array.isArray(files) ? files : [files]);
  }

  private _processFiles(files: File[]) {
    if (this._interceptDropForProvider(files)) return;
    const accepted = this._partitionByFiletype(files, f => f.name, f => f.type);
    const handles = accepted.map(f => new AsFileHandle(f));
    if (handles.length) this._dispatch(handles);
  }

  private _showLoadedState(handles: AsFileHandle[]) {
    if (!handles.length) return;
    this._currentHandle = handles[0];
    this._currentFiles = handles;
    this._renderLoadedInfo();
    // While an import is open the spinner owns the container. The details are
    // rendered anyway, so _hideImportingState reveals a finished panel rather
    // than an empty one that fills in a frame later.
    if (this._importOpen) return;
    this._emptyState.hidden = true; this._loadedState.hidden = false;
  }

  private _renderLoadedInfo(): void {
    const handles = this._currentFiles;
    if (!handles.length) return;
    if (this.hasAttribute('minimal-file-list')) {
      // The count is only meaningful with `multiple` — without it, this input
      // holds at most one file, so a persistent "1 file uploaded" reads as if
      // the count were stuck rather than reflecting the (fixed) single slot.
      if (this.hasAttribute('multiple')) {
        this._fileNameText.setAttribute('default-text', `${handles.length} file${handles.length === 1 ? '' : 's'} uploaded`);
        this._fileNameText.setAttribute('dict-key', 'fileIO.upload.filesUploadedCount');
        this._fileNameText.vars = [String(handles.length)];
      } else {
        this._fileNameText.setAttribute('default-text', 'File uploaded');
        this._fileNameText.setAttribute('dict-key', 'fileIO.upload.fileUploaded');
        this._fileNameText.vars = null;
      }
      this._fileSizeText.textContent = '';
    } else {
      this._fileNameText.removeAttribute('dict-key');
      this._fileNameText.removeAttribute('default-text');
      this._fileNameText.textContent = handles.length === 1 ? handles[0].name : `${handles[0].name} +${handles.length - 1} more`;
      this._fileSizeText.textContent = handles.length === 1 ? `${(handles[0].size / 1024).toFixed(1)} KB` : '';
    }
  }

  /** File extension, lowercased — the most reliable mesh-vs-other discriminator (MIME for STL is unreliable). */
  private _ext(h: AsFileHandle): string {
    return h.extension || h.type.toLowerCase();
  }

  /**
   * Fire a unified as-data-import event at the end of a successful import. We don't
   * fire an import-started here: the native file picker gives no cancel signal, so we
   * can't track the full lifecycle. Mesh-vs-file is not distinguished at the event
   * level — the fileInfo array carries each file's type and size. The subcategory is
   * 'grabcad' when a host import provider handled the selection, otherwise 'local'.
   */
  private _dispatchImportDone(handles: AsFileHandle[]): void {
    if (!handles.length) return;
    const fileInfo: FileInfoArr = handles.map(h => ({ sizeBytes: h.size, fileType: this._ext(h) }));
    const env = internalData.appEnv;
    // subcategory reflects who actually handled the import: the host env only
    // when its provider claimed this category, otherwise the native 'local' path.
    const subcategory = this._importProvider() ? env : 'local';
    sendEv(this, 'as-data-import', {
      detail: { subcategory, fileInfo }
    });
  }

  /** Notify consumers of a selection change: stock `change` (so onchange/@change works
   *  like native inputs) plus the richer `as-files-selected` / `as-files-change`
   *  carrying the handles (and the deprecated `as-file-selected` alias, kept for
   *  existing listeners). */
  private _emitSelected(files: AsFileHandle[]): void {
    sendEv(this, 'change');
    sendEv(this, 'as-files-selected', { detail: { files } });
    sendEv(this, 'as-files-change', { detail: { files } });
    sendEv(this, 'as-file-selected', { detail: { files } });
  }

  private async _dispatch(handles: AsFileHandle[]) {
    const files = this.hasAttribute('multiple') ? handles : [handles[0]];
    if (!files[0]) return;

    // Replacing an existing file is destructive (the old binary at this slot is gone),
    // so confirm BEFORE swapping the UI or emitting any event — cancelling must be a no-op.
    const previousToken = this._currentToken;
    const isReplacement = !!previousToken;
    if (isReplacement && !await this._confirmReplace()) return;

    // Opens the import for the native path, where nothing has reported progress
    // and the wait starts here. On the provider path the import is already open
    // (conversion reported first) and this is a no-op — same bracket either way,
    // so a listener sees the same sequence in every environment.
    this._beginImport(files.length);

    this._showLoadedState(files);
    this._emitSelected(files);

    // Multi-selections travel as File[] so the configurator can upload every
    // file (token array for `file[]` params; scalar params take the first
    // with a console warning there).
    const payload: File | File[] = files.length === 1
      ? files[0].toFile()
      : files.map(f => f.toFile());

    if (this.hasAttribute('auto-upload-file')) {
      // Upload-only mode: do not set a param value or trigger configure.
      // First upload (empty → file) is undoable via 'input' history.
      // Replacing an existing file is NOT undoable (old binary is gone from that fileId slot).
      const fileId = this._fileId();
      this._uploadPending = true;
      sendEv(window, 'as-intrn-upload-file', {
        detail: {
          file: payload,
          fileParamName: fileId,
          configure: false,
          uploadOnly: true,
          resolve: (token: string, tokens?: string[]) => {
            this._currentToken = token;
            this._currentTokens = tokens ?? [token];
            sendEv(this, 'as-file-uploaded', { detail: { token, tokens: tokens ?? [token], fileId } });
            // Record undo history only for first upload (empty → file).
            // Replacing is non-undoable because the old binary at fileId is gone.
            if (!previousToken) {
              if (!this.id) { const tag = this.tagName.toLowerCase(); this.id = `${tag}-upload-${Math.random().toString(36).slice(2,7)}`; }
              sendEv(this, 'as-input-change', {
                detail: { type: 'file', value: token, reason: 'user', phase: 'change' }
              });
            }
            this._dispatchImportDone(files);
            this._uploadPending = false;
            this._endImport(true);
          },
          reject: (err: any) => { console.error(err); this._uploadPending = false; this._endImport(false); },
        }
      });
      return;
    }

    const paramName = this.getAttribute('trcad-param');
    if (paramName) {
      this._uploadPending = true;
      sendEv(window, 'as-intrn-upload-file', {
        detail: {
          file: payload,
          fileParamName: paramName,
          configure: this.hasAttribute('configure-on-change'),
          resolve: (token: string, _parmRes?: any, tokens?: string[]) => {
            this._currentToken = token;
            this._currentTokens = tokens ?? [token];
            this._uploadedHandles.set(this._currentTokens.join('|'), files);
            if (isReplacement) {
              sendEv(window, 'as-history-clear');
            }
            this._dispatchImportDone(files);
            this._uploadPending = false;
            this._endImport(true);
          },
          reject: (err: any) => { console.error(err); this._uploadPending = false; this._endImport(false); }
        }
      });
    } else {
      // No server upload (no trcad-param): the import is complete once shown locally.
      this._dispatchImportDone(files);
      this._endImport(true);
    }
  }

  /** Effective file-type filter, single-sourced from `accept`. Its syntax
   *  (extensions, MIME types, and MIME wildcards) is exactly what
   *  _matchesFiletype parses, so one value drives the native picker, drop/pick
   *  validation, and the "Accepts:" label. The legacy `filetype` attribute
   *  still overrides when present, with a one-time deprecation warning. The
   *  all-types accept wildcard counts as "no filter".
   *
   *  Fallback: with no explicit accept/filetype but an explicit `category` of
   *  mesh|image, derive the filter from CATEGORY_EXTENSIONS so the declared kind
   *  is actually enforced (and rejected files get feedback) even outside a host
   *  import provider. Only mesh/image have meaningful whitelists; the implicit
   *  default category does NOT trigger this (returns '' so behaviour is unchanged
   *  for components that never set `category`). */
  private _effectiveFiletype(): string {
    const filetype = (this.getAttribute('filetype') ?? '').trim();
    if (filetype) {
      if (!this._warnedFiletypeDeprecated) {
        this._warnedFiletypeDeprecated = true;
        console.warn(
          `[as-file-input] the 'filetype' attribute is deprecated; use 'accept' (same syntax) instead.`
        );
      }
      return filetype;
    }
    const accept = (this.getAttribute('accept') ?? '').trim();
    if (accept && accept !== '*/*') return accept;

    const category = (this.getAttribute('category') ?? '').trim().toLowerCase();
    if (category === 'mesh' || category === 'image') {
      return CATEGORY_EXTENSIONS[category].map(e => `.${e}`).join(',');
    }
    return '';
  }

  private _matchesFiletype(filename: string, mimeType: string): boolean {
    const filter = this._effectiveFiletype();
    if (!filter) return true;
    const filters = filter.split(',').map(s => s.trim().toLowerCase());
    const ext = (filename.split('.').pop() ?? '').toLowerCase();
    const mime = mimeType.toLowerCase();
    return filters.some(f => f.startsWith('.') ? ext === f.slice(1) : f.endsWith('/*') ? mime.startsWith(f.slice(0, -2) + '/') : f.includes('/') ? mime === f : ext === f);
  }
}

if (!customElements.get(AsFileInput.tag)) customElements.define(AsFileInput.tag, AsFileInput);
